import React, { useState, useEffect } from "react";
import {
  Popover,
  Box,
  Typography,
  Divider,
  Button,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import MasterViewController from "../../redux/actions/masterApiActions";

const FilterEmployeeDrawer = ({ anchorEl, handleClose, employees, onApply }) => {
  const open = Boolean(anchorEl);
  const dispatch = useDispatch();
  const { getDepartments, getDesignations } = MasterViewController();
  const { departments, designations } = useSelector((state) => state.master);

  const [filters, setFilters] = useState({
    departments: [],
    designations: [],
    status: [],
    startDate: "",
    endDate: "",
  });

  useEffect(() => {
    dispatch(getDepartments());
    dispatch(getDesignations());
  }, [dispatch]);

  const toggleFilter = (field, value) => {
    setFilters((prev) => {
      const values = prev[field];
      return {
        ...prev,
        [field]: values.includes(value)
          ? values.filter((v) => v !== value)
          : [...values, value],
      };
    });
  };

  const handleApply = () => {
    let filtered = [...employees];

    if (filters.departments.length > 0) {
      filtered = filtered.filter(
        (emp) =>
          emp?.profile_data?.department_name &&
          filters.departments.includes(emp.profile_data.department_name)
      );
    }

    if (filters.designations.length > 0) {
      filtered = filtered.filter(
        (emp) =>
          emp?.profile_data?.designation_name &&
          filters.designations.includes(emp.profile_data.designation_name)
      );
    }

    if (filters.status.length > 0) {
      filtered = filtered.filter((emp) =>
        filters.status.includes(emp.is_active ? "Active" : "Inactive")
      );
    }

    if (filters.startDate) {
      filtered = filtered.filter(
        (emp) =>
          emp?.profile_data?.joining_date &&
          new Date(emp.profile_data.joining_date) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      filtered = filtered.filter(
        (emp) =>
          emp?.profile_data?.joining_date &&
          new Date(emp.profile_data.joining_date) <= new Date(filters.endDate)
      );
    }

    onApply(filtered);
    handleClose();
  };

  const handleClear = () => {
    setFilters({
      departments: [],
      designations: [],
      status: [],
      startDate: "",
      endDate: "",
    });
    onApply(employees);
    handleClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      PaperProps={{
        sx: { width: 320, display: "flex", flexDirection: "column" }, // fixed width & flex layout
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: "1px solid #eee",
        }}
      >
        <Typography variant="h6">Filters</Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {/* Departments */}
        <Typography variant="subtitle1" gutterBottom>
          Departments
        </Typography>
        <FormGroup>
          {departments?.map((dept) => (
            <FormControlLabel
              key={dept.id}
              control={
                <Checkbox
                  checked={filters.departments.includes(dept.name)}
                  onChange={() => toggleFilter("departments", dept.name)}
                />
              }
              label={dept.name}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Designations */}
        <Typography variant="subtitle1" gutterBottom>
          Designations
        </Typography>
        <FormGroup>
          {designations?.map((des) => (
            <FormControlLabel
              key={des.id}
              control={
                <Checkbox
                  checked={filters.designations.includes(des.name)}
                  onChange={() => toggleFilter("designations", des.name)}
                />
              }
              label={des.name}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Status */}
        <Typography variant="subtitle1" gutterBottom>
          Status
        </Typography>
        <FormGroup>
          {["Active", "Inactive"].map((st) => (
            <FormControlLabel
              key={st}
              control={
                <Checkbox
                  checked={filters.status.includes(st)}
                  onChange={() => toggleFilter("status", st)}
                />
              }
              label={st}
            />
          ))}
        </FormGroup>

        <Divider sx={{ my: 2 }} />

        {/* Dates */}
        <Typography variant="subtitle1" gutterBottom>
          Joining Date
        </Typography>
        <TextField
          type="date"
          label="Start Date"
          value={filters.startDate}
          onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          type="date"
          label="End Date"
          value={filters.endDate}
          onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />
      </Box>

      {/* Footer */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          p: 2,
          borderTop: "1px solid #eee",
        }}
      >
        <Button onClick={handleClear} color="secondary">
          Clear
        </Button>
        <Button variant="contained" onClick={handleApply}>
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default FilterEmployeeDrawer;
